// Подтверждение удачного действия: одно место на весь кабинет.
//
// ## Зачем мост, а не вызов из экрана
//
// До 04.09.2026 подтверждение поднимали руками, и подняли его ровно в одном месте из ста
// двадцати трёх: выгрузка в Excel. Остальные сто двадцать два действия проходили молча.
// Человек закрывал ведомость, панель исчезала — и узнать, прошло ли, было негде: он смотрел
// в список и искал глазами, изменилось ли что-нибудь.
//
// Вписывать вызов в каждый экран — значит забыть его в половине и поссорить формулировки:
// «Ведомость закрыта» в одном месте и «Готово» в другом. Поэтому подтверждение объявляется
// РЯДОМ С САМИМ ДЕЙСТВИЕМ, одной строкой `meta`, а показывает его общий обработчик удачи.
//
// ## Почему не всем действиям подряд
//
// Подтверждение нужно там, где итог НЕ виден там, куда человек смотрит: панель закрылась,
// экран сменился, изменилось на соседнем экране. Переключателю в настройках оно не нужно —
// он сам себе подтверждение, а всплывающее окно поверх него превратилось бы в рябь.
// Поэтому объявление добровольное: нет `meta` — нет подтверждения, и это решение экрана.
//
// ## Почему мост, а не контекст
//
// Обработчик удачи живёт в клиенте запросов, вне дерева React, и до контекста дотянуться не
// может. Тот же приём уже стоит у выхода из системы (`setForgetHandler`): слой `shared/` не
// знает про клиент запросов, иначе получилось бы кольцо импортов.

/** Что показать человеку: одна короткая фраза о том, что произошло. */
export interface Подтверждение {
  /** Главное: что случилось. «Ведомость закрыта», а не «Успех». */
  что: string;
  /** Подробность, если она нужна человеку. Обычно не нужна. */
  подробность?: string;
}

type Показать = (весть: Подтверждение) => void;

let показать: Показать | null = null;

/**
 * Кто показывает подтверждения. Ставит провайдер при сборке дерева, снимает при разборке.
 *
 * До первой установки и после снятия подтверждения просто не показываются: экран, который
 * зовёт это из проверки без провайдера, не должен от этого падать.
 */
export function назначитьПоказ(обработчик: Показать | null): void {
  показать = обработчик;
}

/** Показать подтверждение, если есть кому. */
export function подтвердить(весть: Подтверждение): void {
  показать?.(весть);
}

/**
 * Объявление подтверждения у мутации: `meta: подтверждение('Ведомость закрыта')`.
 *
 * Отдельная функция, а не голый объект, ровно ради одного: имя ключа не разъезжается по ста
 * двадцати трём местам, и опечатка в нём становится ошибкой типов, а не тихой тишиной.
 *
 * Фразу можно посчитать по вводу: у решения по чеку четыре исхода на одну мутацию, и
 * «Решение записано» вместо «Чек принят» — это отписка, а не подтверждение. Вернуть `null`
 * значит промолчать: бывают исходы, о которых говорить нечего.
 *
 * Подробность тоже можно посчитать: «Состояние изменено» всегда одно, а «Теперь: вкладка
 * «Резерв»» зависит от того, куда человек попал, и иногда её нет вовсе (`undefined`).
 */
export function подтверждение<Ввод = unknown, Данные = unknown>(
  что: string | ((ввод: Ввод, данные: Данные) => string | null),
  подробность?: string | ((ввод: Ввод, данные: Данные) => string | undefined),
) {
  const считаетсяПоДанным = typeof что === 'function' || typeof подробность === 'function';
  const весть: ОбъявлениеПодтверждения = считаетсяПоДанным
    ? {
        посчитать: (ввод, данные) => {
          const слова =
            typeof что === 'function' ? что(ввод as Ввод, данные as Данные) : что;
          if (!слова) return null;
          return {
            что: слова,
            подробность:
              typeof подробность === 'function'
                ? подробность(ввод as Ввод, данные as Данные)
                : подробность,
          };
        },
      }
    : { что: что as string, подробность: подробность as string | undefined };
  return { подтверждение: весть };
}

interface ОбъявлениеПосчитать {
  посчитать: (ввод: unknown, данные: unknown) => Подтверждение | null;
}

type ОбъявлениеПодтверждения = Подтверждение | ОбъявлениеПосчитать;

/** Достать объявление из `meta` мутации. Пусто — действие подтверждается молча. */
export function изМеты(meta: unknown, ввод?: unknown, данные?: unknown): Подтверждение | null {
  if (!meta || typeof meta !== 'object') return null;
  const весть = (meta as { подтверждение?: unknown }).подтверждение;
  if (!весть || typeof весть !== 'object') return null;

  const посчитать = (весть as Partial<ОбъявлениеПосчитать>).посчитать;
  if (typeof посчитать === 'function') return посчитать(ввод, данные);

  const что = (весть as { что?: unknown }).что;
  if (typeof что !== 'string' || что.length === 0) return null;
  const подробность = (весть as { подробность?: unknown }).подробность;
  return { что, подробность: typeof подробность === 'string' ? подробность : undefined };
}
